import React from "react";
import { motion } from "framer-motion";
import { LockIcon, ZapIcon, ShieldCheckIcon, ArrowRightIcon } from "lucide-react";
import { trackMetaEvent } from "../analytics/metaPixel";
import { trustBadges, PRICE_CURRENCY, PRICE_VALUE } from "./data";
export const CHECKOUT_URL = "https://bibliotecatdahyautismoinfantil.impultienda.ar/checkout";
const PRODUCT = {
  content_name: "Pequeños Pasos, Grandes Logros",
  content_category: "Colección digital educativa"
};

/** Every CTA above the offer scrolls to the price block instead of leaving the page. */
export function scrollToOffer() {
  trackMetaEvent("Lead", PRODUCT);
  const el = document.getElementById("oferta");
  if (el) el.scrollIntoView({
    behavior: "smooth",
    block: "start"
  });
}

/** Only the offer CTA and the final CTA leave for the checkout. */
export function goToCheckout() {
  trackMetaEvent("InitiateCheckout", {
    ...PRODUCT,
    currency: PRICE_CURRENCY,
    value: PRICE_VALUE
  });
  window.open(CHECKOUT_URL, "_blank", "noopener,noreferrer");
}
const badgeIcon: Record<string, React.ReactNode> = {
  lock: <LockIcon className="h-4 w-4" aria-hidden data-id="element-226" data-mp="122qyqz4q5zip" />,
  zap: <ZapIcon className="h-4 w-4" aria-hidden data-id="element-227" data-mp="121fn6d51fnh1" />,
  shield: <ShieldCheckIcon className="h-4 w-4" aria-hidden data-id="element-228" data-mp="1723l7irop4kr" />
};
export function TrustBadges({
  className = "",
  tone = "muted"
}: {
  className?: string;
  tone?: "muted" | "light";
}) {
  const text = tone === "light" ? "text-white/80" : "text-[var(--muted)]";
  const icon = tone === "light" ? "text-white" : "text-[var(--accent)]";
  return <ul className={`flex flex-wrap items-center justify-center gap-x-5 gap-y-2 ${className}`} data-id="element-229" data-mp="jd0xpuadchic">
      {trustBadges.map(b => <li key={b.label} className={`flex items-center gap-1.5 text-xs font-medium sm:text-sm ${text}`} data-id="element-230" data-mp="s5mbevz57y0b">
          <span className={icon} data-id="element-231" data-mp="3rrt01vse0qdg">{badgeIcon[b.icon]}</span>
          {b.label}
        </li>)}
    </ul>;
}

/** Primary CTA that scrolls down to the offer. */
export function CTAButton({
  children,
  className = "",
  onClick = scrollToOffer
}: {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
}) {
  return <motion.button type="button" onClick={onClick} whileHover={{
    scale: 1.015
  }} whileTap={{
    scale: 0.985
  }} transition={{
    type: "spring",
    stiffness: 420,
    damping: 24
  }} className={`group inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[var(--accent)] px-7 py-4 text-[15px] font-extrabold uppercase tracking-[0.02em] text-white shadow-[0_16px_34px_-14px_rgba(41,111,232,0.7)] hover:bg-[#245dc3] focus:outline-none focus-visible:ring-4 focus-visible:ring-[var(--accent)]/30 sm:w-auto sm:text-base ${className}`} data-id="element-232" data-mp="1cpipabjhicem">
      {children}
      <ArrowRightIcon className="h-5 w-5 transition-transform duration-200 ease-out group-hover:translate-x-1" aria-hidden data-id="element-233" data-mp="p2al5wxsl9e6" />
    </motion.button>;
}

/** Section wrapper for consistent vertical rhythm + max width. */
export function Section({
  id,
  children,
  className = "",
  width = "wide"
}: {
  id?: string;
  children: React.ReactNode;
  className?: string;
  width?: "wide" | "narrow";
}) {
  return <section id={id} className={`w-full scroll-mt-4 px-4 py-12 sm:px-8 sm:py-20 ${className}`} data-id="element-234" data-mp="1y17xetduzmys">
      <div className={`mx-auto w-full ${width === "wide" ? "max-w-6xl" : "max-w-3xl"}`} data-id="element-235" data-mp="230aqdfgke2qe">
        {children}
      </div>
    </section>;
}

/** Fade / slide up on scroll into view. */
export function Reveal({
  children,
  delay = 0,
  y = 20,
  className = ""
}: {
  children: React.ReactNode;
  delay?: number;
  y?: number;
  className?: string;
}) {
  return <motion.div initial={{
    opacity: 0,
    y
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: "-60px"
  }} transition={{
    duration: 0.5,
    delay,
    ease: [0.23, 1, 0.32, 1]
  }} className={className} data-id="element-236" data-mp="2lxlq1163yy6d">
      {children}
    </motion.div>;
}
export function Eyebrow({
  children,
  tone = "accent"
}: {
  children: React.ReactNode;
  tone?: "accent" | "green" | "light";
}) {
  const color = tone === "green" ? "text-[var(--accent-green)]" : tone === "light" ? "text-white/70" : "text-[var(--accent)]";
  return <p className={`mb-3 text-[11px] font-extrabold uppercase tracking-[0.18em] ${color}`} data-id="element-237" data-mp="1btlw9na0i7rd">
      {children}
    </p>;
}
export function SectionTitle({
  children,
  className = ""
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <h2 className={`font-serif-display text-[1.75rem] font-semibold leading-[1.1] tracking-[-0.02em] text-[var(--ink)] sm:text-[2.4rem] ${className}`} data-id="element-238" data-mp="24juiprbkzebr">
      {children}
    </h2>;
}